import { Add, Delete } from '@mui/icons-material'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import {
  Box,
  Checkbox,
  FormControl,
  Grid,
  InputLabel,
  ListSubheader,
  MenuItem,
  Select,
  Slider,
  styled,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { FormFieldType } from '../../../enums'
import {
  CollectionFormFieldList,
  DefaultValueIncludedFieldList,
  FormField,
  formFieldsDefaultValues,
  IOption,
  NotFieldTypes,
  IChecklistItem,
} from '../../../generated-types/form-field'
import { generateUUID } from '../../../utils/function'
import { DynamicTextTypography } from 'src/components/DynamicTextTypography'
import { MUIThemeColors } from 'src/mui-theme'
import { getThemeColor } from 'src/utils'

interface IFormValidation {
  selectedSetting: FormField
  changeSetting: (value: any) => void
  onDelete: () => void
}

const initialOption = { label: '', value: '' }

const FormValidation: React.FC<IFormValidation> = ({ selectedSetting, changeSetting, onDelete }) => {
  const [option, setOption] = useState<IOption>(initialOption)
  const [checklistQuestion, setChecklistQuestion] = useState('')
  const { t: translate } = useTranslation(['form-schema', 'common'])
  const requiredRef = useRef(null)
  const disabledRef = useRef(null)
  const onChange = useCallback(
    (value, fieldName) => {
      const fieldValues = selectedSetting
      fieldValues[fieldName] = value
      changeSetting(fieldValues)
    },
    [selectedSetting],
  )

  useEffect(() => {
    if (requiredRef?.current) {
      const element = requiredRef?.current as Element
      element.getElementsByClassName('PrivateSwitchBase-input')[0]['checked'] = selectedSetting?.isRequired
    }
  }, [selectedSetting?.identifier])

  useEffect(() => {
    if (disabledRef?.current) {
      const element = disabledRef?.current as Element
      element.getElementsByClassName('PrivateSwitchBase-input')[0]['checked'] = selectedSetting?.disabled
    }
  }, [selectedSetting?.identifier, disabledRef])

  return selectedSetting ? (
    <Box padding={1}>
      {[FormFieldType.richText, FormFieldType.instructionImage, FormFieldType.instructionFile].includes(
        selectedSetting.type,
      ) ? (
        <Box marginTop={1} padding={1}>
          <Typography color={'gray'} variant="subtitle2" borderBottom={'1px solid gray'} paddingBottom={1}>
            {translate(`${Object.keys(FormFieldType).find(key => FormFieldType[key] === selectedSetting.type)}`, {
              ns: 'form-schema',
            })}
          </Typography>
          <Typography color={'gray'} variant="caption" paddingTop={1}>
            {translate('edit-inline-hint')}
          </Typography>
          {selectedSetting.type === FormFieldType.instructionImage && (
            <Box paddingTop={2}>
              <Box display="flex" alignItems="center" gap={0.5} mb={1}>
                <Typography variant="caption" color="text.secondary">
                  {translate('image-size', { ns: 'form-schema' })} — {selectedSetting?.imageWidth ?? 100}%
                </Typography>
                <Tooltip title={`${translate('image-size-info', { ns: 'form-schema' })}`} placement="right">
                  <InfoOutlinedIcon color="action" style={{ fontSize: 16, cursor: 'pointer' }} />
                </Tooltip>
              </Box>
              <Slider
                value={selectedSetting?.imageWidth ?? 100}
                min={10}
                max={100}
                step={5}
                marks={[
                  { value: 10, label: '10%' },
                  { value: 50, label: '50%' },
                  { value: 100, label: '100%' },
                ]}
                valueLabelDisplay="auto"
                valueLabelFormat={v => `${v}%`}
                onChange={(_evt, val) => onChange(val as number, 'imageWidth')}
              />
            </Box>
          )}
          <Box display={'flex'} flexDirection={'row'} paddingTop={1}>
            <Checkbox
              checked={selectedSetting?.showInReport ?? false}
              onChange={evt => onChange(evt.target.checked, 'showInReport')}
            />
            <Box paddingTop={1}>
              <Typography>{translate('show-in-report')}</Typography>
            </Box>
          </Box>
        </Box>
      ) : (
        <>
          <Box marginTop={1} padding={1}>
            <Typography color={'gray'} variant="subtitle2" borderBottom={'1px solid gray'} paddingBottom={1}>
              {translate('general-settings')}
            </Typography>
            <Box paddingTop={2}>
              <TextField
                value={selectedSetting?.name}
                label={translate('name', { ns: 'common' })}
                variant="outlined"
                onChange={evt => onChange(evt.target.value, 'name')}
              />
            </Box>
            {DefaultValueIncludedFieldList.includes(selectedSetting.type) && (
              <>
                <Box marginTop={1}>
                  <FormControl fullWidth>
                    <InputLabel id="demo-simple-select-label">{translate('default-values')}</InputLabel>
                    <Select
                      key={selectedSetting?.identifier}
                      fullWidth={true}
                      labelId="demo-simple-select-label"
                      id="demo-simple-select"
                      label={translate('default-values')}
                      value={selectedSetting?.defaultValue || ''}
                      onChange={evt => {
                        onChange(evt.target.value, 'defaultValue')
                      }}
                    >
                      {/* <MenuItem value={''}>--{translate('none', { ns: 'form-schema' })}--</MenuItem>*/}
                      {/* <ListSubheader ><Typography fontSize={16} borderBottom={'1px solid gray'} marginTop={1}>{translate('facility', { ns: 'form-schema' })}</Typography></ListSubheader>
                                    <MenuItem value={formFieldsDefaultValues.facilityName}><Typography fontSize={14}>{translate('name', { ns: 'common' })}</Typography></MenuItem> */}
                      <ListSubheader>
                        <Typography fontSize={16} borderBottom={'1px solid gray'} marginTop={1}>
                          {translate('site-location', { ns: 'form-schema' })}
                        </Typography>
                      </ListSubheader>
                      <MenuItem value={formFieldsDefaultValues.siteLocationName}>
                        <Typography fontSize={14}>{translate('name', { ns: 'common' })}</Typography>
                      </MenuItem>
                      <MenuItem value={formFieldsDefaultValues.siteNumber}>
                        <Typography fontSize={14}>{translate('site-number', { ns: 'form-schema' })}</Typography>
                      </MenuItem>
                      <ListSubheader>
                        <Typography fontSize={16} borderBottom={'1px solid gray'} marginTop={1}>
                          {translate('equipment', { ns: 'common' })}
                        </Typography>
                      </ListSubheader>
                      {/* <MenuItem value={formFieldsDefaultValues.productionNumber}>{translate('productionNumber', { ns: 'form-schema' })}</MenuItem> */}
                      <MenuItem value={formFieldsDefaultValues.orderNumber}>
                        <Typography fontSize={14}>{translate('orderNumber', { ns: 'form-schema' })}</Typography>
                      </MenuItem>
                      <MenuItem value={formFieldsDefaultValues.netType}>
                        <Typography fontSize={14}>{translate('component-type')}</Typography>
                      </MenuItem>
                      {/* <MenuItem value={formFieldsDefaultValues.netFunction}>{translate('net-function')}</MenuItem> */}
                      {/* <MenuItem value={formFieldsDefaultValues.perimeter}>{translate('perimeter')}</MenuItem> */}
                      <MenuItem value={formFieldsDefaultValues.cagePlacement}>{translate('cage-placement')}</MenuItem>
                      <MenuItem value={formFieldsDefaultValues.PositionNumber}>
                        {translate('position', { ns: 'position' })}
                      </MenuItem>
                      <MenuItem value={formFieldsDefaultValues.resourcesName}>
                        <Typography fontSize={14}>{translate('name', { ns: 'common' })}</Typography>
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Box>
                <Box display={'flex'} flexDirection={'row'} marginTop={1}>
                  <Checkbox
                    ref={disabledRef}
                    defaultChecked={selectedSetting?.disabled}
                    checked={selectedSetting?.disabled ?? false}
                    onChange={evt => {
                      onChange(evt.target.checked, 'disabled')
                    }}
                  />
                  <Box marginTop={1}>
                    <Typography>{translate('write-protected')}</Typography>
                  </Box>
                </Box>
              </>
            )}
          </Box>
          {!NotFieldTypes.includes(selectedSetting.type) && (
            <Box marginTop={1} padding={1}>
              <Typography color={'gray'} variant="subtitle2" borderBottom={'1px solid gray'} paddingBottom={1}>
                {translate('validations')}
              </Typography>
              <Box display={'flex'} flexDirection={'row'}>
                <Checkbox
                  ref={requiredRef}
                  defaultChecked={selectedSetting?.isRequired}
                  checked={selectedSetting?.isRequired ?? false}
                  onChange={evt => onChange(evt.target.checked, 'isRequired')}
                />
                <Box paddingTop={1}>
                  <Typography>{translate('is-required')}</Typography>
                </Box>
              </Box>
            </Box>
          )}
          {CollectionFormFieldList.includes(selectedSetting.type) && (
            <Box marginTop={1}>
              <Typography
                color={'gray'}
                variant="subtitle2"
                borderBottom={'1px solid gray'}
                paddingBottom={1}
                marginBottom={1}
              >
                {translate('options')}
              </Typography>
              {selectedSetting.options?.map((option: IOption, index: number) => {
                return (
                  <Grid container key={index + option.value} md={12}>
                    <Grid item sm={10}>
                      <StyledTextField
                        value={option.label}
                        placeholder={translate('label')}
                        onChange={evt => {
                          const optionList = selectedSetting.options || []
                          if (optionList[index].label == optionList[index].value) {
                            optionList[index] = { label: evt.target.value, value: evt.target.value }
                          } else {
                            optionList[index] = { label: evt.target.value, value: optionList[index].value }
                          }
                          onChange(optionList, 'options')
                        }}
                      />
                    </Grid>
                    {/* Value section is hidden for now. It will be removed/available after clarifying 
                    if there are scenarios we need to display different values in schema report */}
                    {/* <Grid item sm={5}>
                      <TextField
                        value={option.value}
                        placeholder={translate('value')}
                        onChange={evt => {
                          const optionList = selectedSetting.options || []
                          optionList[index] = { value: evt.target.value, label: optionList[index].label }
                          onChange(optionList, 'options')
                        }}
                      />
                    </Grid> */}
                    <Grid item sm={2}>
                      <Box marginTop={2} marginLeft={1}>
                        <Delete
                          color="error"
                          style={{ cursor: 'pointer' }}
                          onClick={evt => {
                            const optionList = selectedSetting.options || []
                            optionList.splice(index, 1)
                            onChange(optionList, 'options')
                          }}
                        />
                      </Box>
                    </Grid>
                  </Grid>
                )
              })}
              <Grid container md={12}>
                <Grid item sm={10}>
                  <TextField
                    value={option.label}
                    placeholder={translate('label')}
                    onChange={evt => {
                      if (option.label == option.value) {
                        setOption({ label: evt.target.value, value: evt.target.value })
                      } else {
                        setOption({ ...option, ...{ label: evt.target.value } })
                      }
                    }}
                  />
                </Grid>
                {/* Value section is hidden for now. It will be removed/available after clarifying 
                    if there are scenarios we need to display different values in schema report */}
                {/* <Grid item sm={5}>
                  <TextField
                    value={option.value}
                    placeholder={translate('value')}
                    onChange={evt => {
                      setOption({ ...option, ...{ value: evt.target.value } })
                    }}
                  />
                </Grid> */}
                <Grid item sm={2}>
                  <Box marginTop={2} marginLeft={1}>
                    <Add
                      onClick={() => {
                        const optionList = selectedSetting.options || []
                        if (option.label == '') {
                          toast.error(translate('optionsAreEmpty'))
                        } else if (optionList.findIndex(o => o.label == option.label) != -1) {
                          toast.error(translate('duplicateOption'))
                        } else {
                          optionList?.push(option)
                          setOption(initialOption)
                          onChange(optionList, 'options')
                        }
                      }}
                    />
                  </Box>
                </Grid>
              </Grid>
            </Box>
          )}
          {selectedSetting.type === FormFieldType.checklist && (
            <Box marginTop={1}>
              <Typography
                color={'gray'}
                variant="subtitle2"
                borderBottom={'1px solid gray'}
                paddingBottom={1}
                marginBottom={1}
              >
                {translate('checklist-items')}
              </Typography>
              {selectedSetting.checklistItems?.map((item: IChecklistItem, index: number) => {
                return (
                  <Grid container key={item.id} md={12} alignItems="center">
                    <Grid item sm={1}>
                      <Typography variant="body2" color="primary">
                        {index + 1}
                      </Typography>
                    </Grid>
                    <Grid item sm={9}>
                      <DynamicTextTypography text={item.question} maxLength={14} />
                    </Grid>
                    <Grid item sm={2}>
                      <Box marginLeft={1}>
                        <Delete
                          fontSize="small"
                          color="error"
                          style={{ cursor: 'pointer' }}
                          onClick={() => {
                            const itemList = selectedSetting.checklistItems || []
                            const updatedList = itemList.filter((_, i) => i !== index)
                            onChange(updatedList, 'checklistItems')
                          }}
                        />
                      </Box>
                    </Grid>
                  </Grid>
                )
              })}
              <Box marginTop={2}>
                <TextField
                  fullWidth
                  value={checklistQuestion}
                  placeholder={translate('type-question-here')}
                  label={translate('question')}
                  onChange={evt => setChecklistQuestion(evt.target.value)}
                  size="small"
                />
              </Box>
              <Box marginTop={1}>
                <StyledAddChecklistButton
                  onClick={() => {
                    const itemList = selectedSetting.checklistItems || []
                    if (checklistQuestion.trim() === '') {
                      toast.error(translate('question-required'))
                    } else {
                      const newItem: IChecklistItem = {
                        id: generateUUID(),
                        question: checklistQuestion.trim(),
                      }
                      const updatedList = [...itemList, newItem]
                      setChecklistQuestion('')
                      onChange(updatedList, 'checklistItems')
                    }
                  }}
                >
                  <Add color="primary" />
                  <Typography color="primary" variant="body2" marginLeft={0.5}>
                    {translate('add-to-checklist')}
                  </Typography>
                </StyledAddChecklistButton>
              </Box>
            </Box>
          )}
        </>
      )}
      <Box
        display={'flex'}
        flexDirection={'row'}
        justifyContent={'center'}
        padding={1}
        marginTop={1}
        border={'1px solid red'}
        borderRadius={1}
        onClick={() => {
          onDelete()
        }}
      >
        <Delete color="warning" />
      </Box>
    </Box>
  ) : (
    <></>
  )
}

const StyledTextField = styled(TextField)({
  '&.MuiOutlinedInput-input': {
    padding: '1px 1px',
    fontSize: '10px',
    backgroundColor: '',
  },
})

const StyledAddChecklistButton = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(1),
  border: '1px solid',
  borderColor: theme.palette.primary.main,
  borderRadius: '4px',
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: getThemeColor(theme, MUIThemeColors.secondaryLight),
  },
}))

export default FormValidation
